import React, { useEffect } from 'react';
import {
    CardOrganization,
    FormContent,
    OrganizationWrapper,
    RequisitieFields,
    Wrapper,
    InputsGroup,
    InputsFIO,
} from './form-buy-order.styles';
import { Typography } from '../../../../shared/ui/typography/typography';
import { InputText } from '../../../../shared/ui/input-text/input-text';
import { InputPhone } from '../../../../shared/ui/input-phone/input-phone';
import { useCurrentUserSelector } from '../../../../entities/current-user/model/selectors';
import { useOrganizationsDetailSharedQuery } from '@entities/organization/libs/hooks/use-organization-detail-shared-query/use-organization-detail-shared-query';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { Field, Form } from 'react-final-form';
import { BuyOrderFormFields, FormBuyOrderProps } from './types';
import { validate } from '../../lib/utils/form-buy-order-validate/form-buy-order-validate';
import { BuyOrderFormKeys } from '../../config/form-edit-order-constants';
import { InputNumber } from '@shared/ui/input-number/input-number';
import { CACHE_KEY_BUY_ORDER } from '../../config/api-constants';
import { useBuyOrderMutation } from '../../api/buy-order-api';
import { useOrderSharedQuery } from '@entities/order/libs/hooks/use-order-shared-query/use-order-shared-query';
import { wordByCount } from '@shared/lib/utils/word-by-count/word-by-count';
import { EmployeeFinder } from '../employee-finder/employee-finder';
import { productCountFromRequests } from '@widgets/buy-manager-order/lib/utils/product-count-from-requests/product-count-from-requests';
import { ListRequests } from '../list-requests/list-requests';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { useRequestsListSharedQuery } from '@entities/request/libs/hooks/use-request-list-shared-query/use-request-list-shared-query';
import { DEFAULT_REQUESTS_LIST_PARAMETERS } from '@entities/request/config/store-constants';

export const FormBuyOrder: React.FC<FormBuyOrderProps> = ({ order, handleSubmitRef }) => {
    const user = useCurrentUserSelector();

    const [getOrder, , resetOrder] = useOrderSharedQuery(order.id);
    const [getOrganization, getOrganizationResult] = useOrganizationsDetailSharedQuery(user?.company?.id || 0);
    const [buyOrder, buyOrderResult] = useBuyOrderMutation({
        fixedCacheKey: CACHE_KEY_BUY_ORDER,
    });
    const [, , resetRequestsList] = useRequestsListSharedQuery(DEFAULT_REQUESTS_LIST_PARAMETERS);
    const organization = getOrganizationResult.data;
    const organizationError = getQueryError(getOrganizationResult);
    const isSendingForm = buyOrderResult.isLoading;
    const isNotFreeProduct = Number(order.product.price) !== 0;

    const handleSubmit = async (fields: BuyOrderFormFields) => {
        await buyOrder({
            orderId: order.id,
            bik: fields[BuyOrderFormKeys.BankBIK],
            bankName: fields[BuyOrderFormKeys.BankName],
            bankAccountNumber: fields[BuyOrderFormKeys.BankPaymentAccount],
            correspondentNumber: fields[BuyOrderFormKeys.BankCorrespondentAccount],
            contactFirstName: fields[BuyOrderFormKeys.ContactFirstName],
            contactLastName: fields[BuyOrderFormKeys.ContactLastName],
            contactMiddleName: fields[BuyOrderFormKeys.ContactMiddleName],
            contactPhone: fields[BuyOrderFormKeys.ContactPhone],
            contactEmail: fields[BuyOrderFormKeys.ContactEmail],
        });
        resetRequestsList();
    };

    const handleValidate = (fields: BuyOrderFormFields) => {
        return validate(fields, isNotFreeProduct);
    };

    const refetchOrder = async () => {
        await getOrder(order.id);
        resetOrder();
    };

    useEffect(() => {
        if (user?.company?.id) {
            getOrganization(user.company?.id);
        }
    }, [user?.company?.id]);

    return (
        <Wrapper>
            <Typography size='h6'>Форма оплаты</Typography>
            <OrganizationWrapper>
                <NetworkStateNew
                    isInitialized={Boolean(organization)}
                    isLoading={
                        getOrganizationResult.isLoading ||
                        getOrganizationResult.isFetching ||
                        getOrganizationResult.isUninitialized
                    }
                    isError={Boolean(organizationError)}
                    hasData={Boolean(organization)}
                    isFiltered={false}
                    errorProps={{
                        title: 'Ошибка загрузки организации',
                        description: organizationError?.message,
                    }}
                    emptyProps={{
                        title: 'Отсутствуют данные организации',
                    }}
                    initialLoaderProps={{
                        style: { height: 92 },
                    }}>
                    <CardOrganization title={organization?.shortName} inn={organization?.inn} kpp={organization?.kpp} />
                </NetworkStateNew>
            </OrganizationWrapper>

            <Form
                subscription={{}}
                onSubmit={handleSubmit}
                validate={handleValidate}
                render={(form) => {
                    handleSubmitRef.current = form.handleSubmit;
                    return (
                        <FormContent>
                            {isNotFreeProduct && (
                                <InputsGroup>
                                    <Typography size='h6'>Банковские реквизиты</Typography>
                                    <RequisitieFields>
                                        <Field
                                            name={BuyOrderFormKeys.BankName}
                                            initialValue={organization?.bankName || ''}
                                            render={({ input, meta }) => {
                                                return (
                                                    <InputText
                                                        required
                                                        label='Наименование банка'
                                                        placeholder='Введите название'
                                                        name={BuyOrderFormKeys.BankName}
                                                        value={input.value}
                                                        error={meta.touched && meta.error}
                                                        disabled={isSendingForm}
                                                        onChange={input.onChange}
                                                    />
                                                );
                                            }}
                                        />
                                        <Field
                                            name={BuyOrderFormKeys.BankPaymentAccount}
                                            initialValue={organization?.bankAccountNumber}
                                            render={({ input, meta }) => (
                                                <InputNumber
                                                    required
                                                    disabled={isSendingForm}
                                                    label='Расчетный счет'
                                                    maxLength={20}
                                                    value={input.value || undefined}
                                                    placeholder='00000000000000000000'
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            )}
                                        />
                                        <Field
                                            name={BuyOrderFormKeys.BankCorrespondentAccount}
                                            initialValue={organization?.correspondentNumber}
                                            render={({ input, meta }) => (
                                                <InputNumber
                                                    required
                                                    disabled={isSendingForm}
                                                    label='Корр. счет'
                                                    maxLength={20}
                                                    placeholder='00000000000000000000'
                                                    value={input.value || undefined}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            )}
                                        />

                                        <Field
                                            name={BuyOrderFormKeys.BankBIK}
                                            initialValue={organization?.bik}
                                            render={({ input, meta }) => (
                                                <InputNumber
                                                    required
                                                    label='БИК'
                                                    disabled={isSendingForm}
                                                    maxLength={9}
                                                    placeholder='000000000'
                                                    value={input.value || undefined}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            )}
                                        />
                                    </RequisitieFields>
                                </InputsGroup>
                            )}

                            <InputsGroup>
                                <Typography size='h6'>Контакты</Typography>

                                <RequisitieFields>
                                    <InputsFIO>
                                        <Field
                                            name={BuyOrderFormKeys.ContactLastName}
                                            initialValue={user?.lastName || ''}
                                            render={({ input, meta }) => (
                                                <InputText
                                                    required
                                                    label='Фамилия'
                                                    placeholder='Введите фамилию'
                                                    name={BuyOrderFormKeys.ContactLastName}
                                                    value={input.value}
                                                    error={meta.touched && meta.error}
                                                    disabled={isSendingForm}
                                                    onChange={input.onChange}
                                                />
                                            )}
                                        />
                                        <Field
                                            name={BuyOrderFormKeys.ContactFirstName}
                                            initialValue={user?.firstName || ''}
                                            render={({ input, meta }) => {
                                                return (
                                                    <InputText
                                                        required
                                                        label='Имя'
                                                        placeholder='Введите имя'
                                                        name={BuyOrderFormKeys.ContactFirstName}
                                                        value={input.value}
                                                        error={meta.touched && meta.error}
                                                        disabled={isSendingForm}
                                                        onChange={input.onChange}
                                                    />
                                                );
                                            }}
                                        />

                                        <Field
                                            name={BuyOrderFormKeys.ContactMiddleName}
                                            initialValue={user?.middleName || ''}
                                            render={({ input, meta }) => {
                                                return (
                                                    <InputText
                                                        label='Отчество'
                                                        placeholder='Введите отчество'
                                                        name={BuyOrderFormKeys.ContactMiddleName}
                                                        value={input.value}
                                                        error={meta.touched && meta.error}
                                                        disabled={isSendingForm}
                                                        onChange={input.onChange}
                                                    />
                                                );
                                            }}
                                        />
                                    </InputsFIO>

                                    <Field
                                        name={BuyOrderFormKeys.ContactEmail}
                                        initialValue={user?.email || ''}
                                        render={({ input, meta }) => (
                                            <InputText
                                                required
                                                label='E-mail'
                                                placeholder='Введите данные'
                                                name={BuyOrderFormKeys.ContactEmail}
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                disabled={isSendingForm}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                    <Field
                                        defaultValue={'+7'}
                                        name={BuyOrderFormKeys.ContactPhone}
                                        initialValue={user?.phoneNumber || ''}
                                        render={({ input, meta }) => (
                                            <InputPhone
                                                required
                                                label='Номер телефона'
                                                name={BuyOrderFormKeys.ContactPhone}
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                disabled={isSendingForm}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                </RequisitieFields>
                            </InputsGroup>

                            <InputsGroup>
                                <Typography size='h6'>Список участников</Typography>
                                <EmployeeFinder order={order} />
                                <Typography size='body2_long' color='neutral_50'>
                                    Всего{' '}
                                    {wordByCount({
                                        count: productCountFromRequests(order.requests),
                                        words: ['продукт', 'продукта', 'продуктов'],
                                    })}
                                </Typography>
                                <ListRequests order={order} refetchOrder={refetchOrder} />
                            </InputsGroup>
                        </FormContent>
                    );
                }}
            />
        </Wrapper>
    );
};
